import styled from '@emotion/styled';
import { Builder } from '@builder.io/react';
import { Colors } from '@zapier/design-system';
import { get } from 'lodash';
import { LIGHT_BACKGROUND_COLORS } from '../../constants';
import { RichTextHTMLString } from '../sections/types';
import { forwardRef } from 'react';
import { useComponentContext } from '../PageBuilderComponent';

type Props = {
  html: RichTextHTMLString;
  backgroundColor?: keyof typeof Colors;
};

// regex to match all values between double curly braces with a limit of 100 characters (e.g. {{foo.bar}})
export const DATA_BINDING_REGEX = /(\\?)(\{\{(.{0,100}?)\}\})/g;

export const bindAsyncDataToHTML = (
  html: string,
  asyncData: Record<string, any>,
) => {
  return html?.replaceAll(
    DATA_BINDING_REGEX,
    (_, escapeCharacter: string, expression: string, contents: string) => {
      if (!!escapeCharacter || !asyncData) {
        return expression;
      }

      const [path, fallbackValue] = contents.split('||').map((s) => s.trim());

      if (!(Builder.isEditing && path.endsWith('help'))) {
        return get(asyncData, path) || fallbackValue || '';
      }

      /**
       * If path is just 'help', show the entire asyncData object,
       * otherwise show the value at the path with .help removed from the end
       */
      const value =
        path === 'help'
          ? asyncData
          : get(asyncData, path.split('.')?.slice(0, -1)) ||
            fallbackValue ||
            '';

      // HTML to show in the editor if 'help' is provided as the key
      return `<code>
      <div>Available keys:</div>
      ${Object.keys(value || {})
        .map((key) => `<li>${key}</li>`)
        .join('')}
      </code>`;
    },
  );
};

export const RichTextHtml = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { asyncData, dataSets } = useComponentContext();
  return (
    <RichText
      dangerouslySetInnerHTML={{
        __html:
          dataSets || Builder.isEditing || Builder.isPreviewing
            ? bindAsyncDataToHTML(props.html, asyncData)
            : props.html,
      }}
      backgroundColor={props.backgroundColor}
      ref={ref}
    />
  );
});

type RichTextProps = Omit<Props, 'html'>;

function getLinkColor({ backgroundColor }: RichTextProps) {
  if (!LIGHT_BACKGROUND_COLORS.some((color) => color === backgroundColor)) {
    return 'inherit';
  }

  return Colors.blue;
}

function getLinkHoverColor({ backgroundColor }: RichTextProps) {
  if (!LIGHT_BACKGROUND_COLORS.some((color) => color === backgroundColor)) {
    return 'inherit';
  }

  return Colors.night;
}

const RichText = styled.div`
  a {
    color: ${getLinkColor};
    text-decoration: underline;

    &:hover {
      color: ${getLinkHoverColor};
      text-decoration: underline;
    }
  }

  ul {
    list-style: none;
    margin-left: 32px;

    li {
      position: relative;

      &:before {
        content: '•';
        font-weight: bold;
        display: flex;
        align-items: baseline;
        position: absolute;
        font-size: 16px;
        margin-left: -1em;
        height: 100%;
      }

      &:not(:last-child) {
        margin: 21px 0;
      }
    }
  }

  ol {
    list-style: none;
    margin-left: 32px;

    li {
      counter-reset: list-1 list-2;
      counter-increment: list-0;

      &:not(:last-child) {
        margin: 21px 0;
      }

      &:before {
        content: counter(list-0, decimal) '. ';
        margin-left: -1em;
      }

      &.ql-indent-1 {
        counter-increment: list-1;
        counter-reset: list-2;

        &:before {
          content: counter(list-1, lower-alpha) '. ';
        }
      }

      &.ql-indent-2 {
        counter-increment: list-2;
        counter-reset: none;

        &:before {
          content: counter(list-2, lower-roman) '. ';
        }
      }
    }
  }

  li.ql-indent-1 {
    padding-left: 2em;
  }

  li.ql-indent-2 {
    padding-left: 4em;
  }

  p:not(:first-of-type) {
    margin-top: 10px;
  }
`;
